import { useState } from "react";
import { toast } from "sonner";
import { Settings2, Globe, Clock, Rows3, CalendarCheck2, Smartphone } from "lucide-react";
import { api, apiError } from "@/lib/api";

const ZONES = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["UTC", "Europe/London", "America/New_York", "Asia/Ho_Chi_Minh", "Asia/Tokyo"];
  }
})();

const selectCls =
  "min-h-[36px] rounded-xl border border-white/10 bg-page px-3 text-xs text-ink outline-none";

const Pill = ({ on, accent, children, ...rest }) => (
  <button
    {...rest}
    aria-pressed={on}
    className="rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors"
    style={on
      ? { borderColor: accent, color: accent, backgroundColor: `${accent}22` }
      : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
  >
    {children}
  </button>
);

export const CalendarSettings = ({ settings, countries, accent, onChange }) => {
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);

  const onTextTest = async () => {
    setTesting(true);
    try {
      await api.post("/calendar/text-test");
      toast.success("Sent — the text should land in a moment");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "That test text did not go out");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-panel p-4" data-testid="calendar-settings">
      <div className="flex flex-wrap items-center gap-3">
        <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest" style={{ color: accent }}>
          <Settings2 className="h-3.5 w-3.5" /> How my calendar behaves
        </p>
        <button
          data-testid="calendar-settings-toggle"
          onClick={() => setOpen((o) => !o)}
          className="ms-auto text-xs text-ink-soft underline decoration-dotted hover:text-ink"
        >
          {open ? "Hide" : "Change"}
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-soft">
              <CalendarCheck2 className="h-3.5 w-3.5" /> Public holidays
            </span>
            <select
              data-testid="settings-country"
              value={settings.holiday_country || ""}
              onChange={(e) => onChange({ holiday_country: e.target.value })}
              className={selectCls}
            >
              <option value="">No country picked</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <Pill
              data-testid="settings-holidays-toggle"
              on={settings.show_holidays}
              accent={accent}
              onClick={() => onChange({ show_holidays: !settings.show_holidays })}
            >
              {settings.show_holidays ? "Showing" : "Hidden"}
            </Pill>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-soft">
              <Globe className="h-3.5 w-3.5" /> My time zone
            </span>
            <select
              data-testid="settings-timezone"
              value={settings.timezone || ""}
              onChange={(e) => onChange({ timezone: e.target.value })}
              className={selectCls}
            >
              <option value="">Not set</option>
              {(settings.timezone && !ZONES.includes(settings.timezone)
                ? [settings.timezone, ...ZONES]
                : ZONES).map((z) => (
                <option key={z} value={z}>{z.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-ink-soft">Weeks start on</span>
            <Pill data-testid="settings-week-mon" on={settings.week_starts === "mon"} accent={accent}
                  onClick={() => onChange({ week_starts: "mon" })}>Monday</Pill>
            <Pill data-testid="settings-week-sun" on={settings.week_starts === "sun"} accent={accent}
                  onClick={() => onChange({ week_starts: "sun" })}>Sunday</Pill>
            <span className="ms-3 inline-flex items-center gap-1.5 text-xs text-ink-soft">
              <Clock className="h-3.5 w-3.5" /> Clock
            </span>
            <Pill data-testid="settings-clock-24" on={settings.clock === "24"} accent={accent}
                  onClick={() => onChange({ clock: "24" })}>24 hour</Pill>
            <Pill data-testid="settings-clock-12" on={settings.clock === "12"} accent={accent}
                  onClick={() => onChange({ clock: "12" })}>12 hour</Pill>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-soft">
              <Smartphone className="h-3.5 w-3.5" /> Mobile for reminders
            </span>
            <input
              data-testid="settings-mobile"
              value={settings.mobile || ""}
              onChange={(e) => onChange({ mobile: e.target.value })}
              placeholder="+14155550123"
              maxLength={24}
              className="min-h-[36px] w-48 rounded-xl border border-white/10 bg-page px-3 text-xs outline-none"
            />
            <Pill
              data-testid="settings-texts-toggle"
              on={settings.reminder_texts !== false}
              accent={accent}
              onClick={() => onChange({ reminder_texts: settings.reminder_texts === false })}
            >
              {settings.reminder_texts === false ? "Texts off" : "Text me too"}
            </Pill>
            <button
              data-testid="settings-text-test"
              onClick={onTextTest}
              disabled={testing}
              className="min-h-[36px] rounded-full border border-white/15 px-3 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand disabled:opacity-50"
            >
              {testing ? "Sending…" : "Send a test text"}
            </button>
            <span className="text-[11px] text-ink-soft" data-testid="settings-texts-note">
              {settings.texts_ready
                ? "Reminders arrive by email and, if you like, by text."
                : "Texts switch on the moment Twilio keys are added — emails go out either way."}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-soft">
              <Rows3 className="h-3.5 w-3.5" /> Rows
            </span>
            <Pill data-testid="settings-density-roomy" on={settings.density === "roomy"} accent={accent}
                  onClick={() => onChange({ density: "roomy" })}>Roomy</Pill>
            <Pill data-testid="settings-density-compact" on={settings.density === "compact"} accent={accent}
                  onClick={() => onChange({ density: "compact" })}>Compact</Pill>
          </div>
        </div>
      )}
    </div>
  );
};
